import { useLogin } from '@refinedev/core'
import { Alert, Spin } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'

import {
  buildOAuthRedirectUri,
  takeOAuthLoginState,
  type CodeLoginProvider,
} from '../../lib/social-auth/oauth-state'

/**
 * Kakao/Naver login callback (`/auth/kakao/callback`·`/auth/naver/callback`, 개발항목 166). The code
 * goes to the server only when the provider returned the CSRF state this tab saved at the start.
 */
export function OAuthCallback({ provider }: { provider: CodeLoginProvider }) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { mutate: login } = useLogin()
  const [error, setError] = useState<string | null>(null)
  const processedRef = useRef(false)

  useEffect(() => {
    if (processedRef.current) return
    processedRef.current = true

    const code = searchParams.get('code')
    const errorParam = searchParams.get('error')
    const returnedState = searchParams.get('state')
    const savedState = takeOAuthLoginState(provider)

    if (errorParam || !code || !savedState || returnedState !== savedState) {
      setError(t('common.errors.loginFailedMessage'))
      return
    }

    login(
      { provider, code, redirectUri: buildOAuthRedirectUri(provider), state: savedState },
      {
        onError: (err) => {
          setError(err.message ?? t('common.errors.loginFailedMessage'))
        },
      },
    )
  }, [searchParams, login, t, provider])

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Alert
          message={t('common.errors.loginFailed')}
          description={error}
          type="error"
          showIcon
          action={<a href="/login">{t('pages.login.title')}</a>}
        />
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Spin size="large" tip={t('pages.login.loggingIn')} />
    </div>
  )
}
